/**
 * Inline-SVG illustrations for the Platform pages and mega-menus.
 *
 * Drawn flat to the Ariviti TABS rules: brand tokens only, no gradients, glows or blur,
 * and Vibrant Orange on exactly one focal element per graphic. Every graphic sits on a
 * Dark Indigo field, so panels are Royal Indigo with Indigo Tint strokes.
 *
 * ponytail: one dispatcher, hand-authored SVG, zero dependencies.
 */

// TABS tokens (keep in sync with packages/design/tailwind-preset.js)
const ROYAL = "#3B3EA9";
const TINT20 = "#D4D5F0";
const TINT50 = "#9D9FD4";
const ORANGE = "#FF4D1C";
const WHITE = "#FFFFFF";

export type GraphicName =
  | "workflow"
  | "agents"
  | "squads"
  | "rag"
  | "integrations"
  | "packs"
  | "shield"
  | "menu-platform"
  | "menu-solutions";

interface Props {
  name: GraphicName;
  className?: string;
  /** false when the graphic shares a view with the orange CTA (heroes) */
  accent?: boolean;
}

// A flat panel used as a card face inside the illustrations.
function Glass({ x, y, w, h, r = 14 }: { x: number; y: number; w: number; h: number; r?: number }) {
  return (
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      rx={r}
      fill={ROYAL}
      stroke={TINT50}
    />
  );
}

function Workflow({ o }: { o: string }) {
  return (
    <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      {/* connectors */}
      <path d="M96 84 C150 84 150 150 200 150" stroke={TINT50} strokeWidth="2" />
      <path d="M96 216 C150 216 150 150 200 150" stroke={TINT50} strokeWidth="2" />
      <path d="M200 150 C260 150 260 96 316 96" stroke={TINT50} strokeWidth="2" />
      <path d="M200 150 C260 150 260 210 316 210" stroke={TINT50} strokeWidth="2" />
      {/* input nodes */}
      <Glass x={40} y={62} w={72} h={44} />
      <rect x={54} y={78} width={44} height={5} rx={2.5} fill={TINT50} />
      <rect x={54} y={90} width={30} height={5} rx={2.5} fill={TINT20} />
      <Glass x={40} y={194} w={72} h={44} />
      <rect x={54} y={210} width={44} height={5} rx={2.5} fill={TINT50} />
      <rect x={54} y={222} width={30} height={5} rx={2.5} fill={TINT20} />
      {/* AI core */}
      <circle cx="200" cy="150" r="34" fill={o} />
      <circle cx="200" cy="150" r="34" stroke={TINT50} />
      <path d="M200 134 l6 12 h-12 z M200 166 l-6 -12 h12 z" fill={WHITE} />
      {/* output nodes */}
      <Glass x={310} y={74} w={72} h={44} />
      <rect x={324} y={90} width={44} height={5} rx={2.5} fill={TINT20} />
      <rect x={324} y={102} width={30} height={5} rx={2.5} fill={TINT20} />
      <Glass x={310} y={188} w={72} h={44} />
      <rect x={324} y={204} width={44} height={5} rx={2.5} fill={TINT20} />
      <rect x={324} y={216} width={30} height={5} rx={2.5} fill={TINT20} />
    </svg>
  );
}

function Agents({ o }: { o: string }) {
  const tools = [
    [200, 44],
    [320, 110],
    [320, 210],
    [200, 268],
    [80, 210],
    [80, 110],
  ];
  return (
    <svg viewBox="0 0 400 312" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      {tools.map(([x, y], i) => (
        <line key={i} x1="200" y1="156" x2={x} y2={y} stroke={TINT50} strokeWidth="2" />
      ))}
      {tools.map(([x, y], i) => (
        <g key={`t${i}`}>
          <rect x={x - 26} y={y - 16} width={52} height={32} rx={9} fill={ROYAL} stroke={TINT50} />
          <circle cx={x - 12} cy={y} r={4} fill={TINT20} />
          <rect x={x - 4} y={y - 3} width={22} height={6} rx={3} fill={TINT50} />
        </g>
      ))}
      {/* agent core */}
      <circle cx="200" cy="156" r="46" fill={o} />
      <circle cx="200" cy="156" r="46" stroke={TINT50} />
      <circle cx="188" cy="150" r="6" fill={WHITE} />
      <circle cx="212" cy="150" r="6" fill={WHITE} />
      <path d="M184 170 q16 14 32 0" stroke={WHITE} strokeWidth="3" fill="none" strokeLinecap="round" />
    </svg>
  );
}

function Squads({ o }: { o: string }) {
  const agents = [
    [70, 70],
    [200, 50],
    [330, 70],
  ];
  return (
    <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      {agents.map(([x, y], i) => (
        <line key={i} x1={x} y1={y + 26} x2="200" y2="222" stroke={TINT50} strokeWidth="2" />
      ))}
      <line x1="96" y1="70" x2="174" y2="60" stroke={TINT50} strokeWidth="2" strokeDasharray="4 5" />
      <line x1="226" y1="60" x2="304" y2="70" stroke={TINT50} strokeWidth="2" strokeDasharray="4 5" />
      {agents.map(([x, y], i) => (
        <g key={`a${i}`}>
          <circle cx={x} cy={y} r="26" fill={ROYAL} />
          <circle cx={x} cy={y} r="26" stroke={TINT50} />
          <circle cx={x - 7} cy={y - 3} r="3.4" fill={WHITE} />
          <circle cx={x + 7} cy={y - 3} r="3.4" fill={WHITE} />
          <path d={`M${x - 9} ${y + 8} q9 8 18 0`} stroke={WHITE} strokeWidth="2.2" fill="none" strokeLinecap="round" />
        </g>
      ))}
      {/* shared goal */}
      <g>
        <rect x="150" y="200" width="100" height="64" rx="16" fill={o} />
        <rect x="150" y="200" width="100" height="64" rx="16" stroke={TINT50} />
        <path d="M186 232 l10 10 20 -22" stroke={WHITE} strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

function Rag({ o }: { o: string }) {
  return (
    <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      {/* documents */}
      <g>
        <rect x="40" y="86" width="70" height="92" rx="8" fill={ROYAL} stroke={TINT50} />
        <rect x="52" y="80" width="70" height="92" rx="8" fill={ROYAL} stroke={TINT50} />
        {[98, 110, 122, 134, 146].map((y) => (
          <rect key={y} x="62" y={y} width={y === 146 ? 30 : 50} height="5" rx="2.5" fill={TINT50} />
        ))}
      </g>
      {/* vector field */}
      <g>
        {[
          [180, 96], [206, 84], [232, 100], [190, 126], [218, 120],
          [176, 150], [204, 150], [232, 150], [190, 178], [218, 176], [200, 204],
        ].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="5" fill={i % 2 ? TINT20 : TINT50} />
        ))}
      </g>
      <path d="M126 128 C150 128 156 132 172 140" stroke={TINT50} strokeWidth="2" />
      <path d="M236 150 C270 150 278 120 300 116" stroke={TINT50} strokeWidth="2" />
      {/* cited answer */}
      <g>
        <rect x="292" y="92" width="88" height="116" rx="14" fill={ROYAL} stroke={TINT50} />
        <path d="M312 124 l8 8 16 -18" stroke={TINT20} strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        {[146, 158, 170].map((y) => (
          <rect key={y} x="306" y={y} width={y === 170 ? 40 : 60} height="5" rx="2.5" fill={TINT20} />
        ))}
        <rect x="306" y="188" width="30" height="10" rx="5" fill={o} />
      </g>
    </svg>
  );
}

function Integrations({ o }: { o: string }) {
  const ports = [
    [70, 60], [330, 60], [50, 150], [350, 150], [70, 240], [330, 240],
  ];
  return (
    <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      {ports.map(([x, y], i) => (
        <line key={i} x1="200" y1="150" x2={x} y2={y} stroke={TINT50} strokeWidth="2" />
      ))}
      {ports.map(([x, y], i) => (
        <g key={`p${i}`}>
          <rect x={x - 22} y={y - 18} width="44" height="36" rx="10" fill={ROYAL} stroke={TINT50} />
          <rect x={x - 8} y={y - 8} width="16" height="16" rx="4" fill={i % 2 ? TINT20 : TINT50} />
        </g>
      ))}
      {/* hub */}
      <rect x="158" y="112" width="84" height="76" rx="18" fill={o} />
      <rect x="158" y="112" width="84" height="76" rx="18" stroke={TINT50} />
      <text x="200" y="156" textAnchor="middle" fontFamily="'Space Grotesk Variable', 'Space Grotesk', sans-serif" fontSize="20" fontWeight="700" fill={WHITE}>MCP</text>
    </svg>
  );
}

function Packs({ o }: { o: string }) {
  const layers = [
    { y: 176, fill: ROYAL },
    { y: 138, fill: TINT50 },
    { y: 100, fill: ROYAL },
  ];
  return (
    <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      {layers.map((l, i) => (
        <g key={i}>
          <path
            d={`M120 ${l.y} L200 ${l.y - 34} L280 ${l.y} L200 ${l.y + 34} Z`}
            fill={l.fill}
                        stroke={TINT50}
          />
        </g>
      ))}
      <circle cx="200" cy="66" r="18" fill={o} stroke={TINT50} />
      <path d="M193 66 l5 5 9 -10" stroke={WHITE} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Shield({ o }: { o: string }) {
  return (
    <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      {/* raw data going in */}
      <g>
        <rect x="30" y="118" width="96" height="64" rx="12" fill={ROYAL} stroke={TINT50} />
        <text x="44" y="146" fontFamily="ui-monospace, monospace" fontSize="12" fill={TINT50}>jane@acme</text>
        <text x="44" y="166" fontFamily="ui-monospace, monospace" fontSize="12" fill={TINT50}>PAN 1234F</text>
      </g>
      <path d="M126 150 H168" stroke={TINT50} strokeWidth="2" />
      {/* shield */}
      <path
        d="M200 78 L252 98 V150 C252 186 228 208 200 222 C172 208 148 186 148 150 V98 Z"
        fill={o}
        stroke={TINT50}
      />
      <path d="M186 150 l10 10 20 -24" stroke={WHITE} strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {/* tokenised out */}
      <path d="M254 150 H286" stroke={TINT50} strokeWidth="2" />
      <g>
        <rect x="286" y="118" width="90" height="64" rx="12" fill={ROYAL} stroke={TINT50} />
        <text x="298" y="146" fontFamily="ui-monospace, monospace" fontSize="12" fill={TINT20}>EMAIL_84d0</text>
        <text x="298" y="166" fontFamily="ui-monospace, monospace" fontSize="12" fill={TINT20}>PAN_c95e</text>
      </g>
    </svg>
  );
}

// Compact motifs for the mega-menu featured panels.
function MenuPlatform({ o }: { o: string }) {
  return (
    <svg viewBox="0 0 260 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      <path d="M56 56 C92 56 92 90 130 90 C168 90 168 124 204 124" stroke={TINT50} strokeWidth="2" />
      <path d="M56 124 C92 124 92 90 130 90" stroke={TINT50} strokeWidth="2" />
      <Glass x={26} y={40} w={56} h={32} r={9} />
      <Glass x={26} y={108} w={56} h={32} r={9} />
      <Glass x={178} y={108} w={56} h={32} r={9} />
      <circle cx="130" cy="90" r="24" fill={o} stroke={TINT50} />
      <path d="M130 78 l5 10 h-10 z M130 102 l-5 -10 h10 z" fill={WHITE} />
    </svg>
  );
}

function MenuSolutions({ o }: { o: string }) {
  return (
    <svg viewBox="0 0 260 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      {[
        [60, 52], [130, 44], [200, 52],
        [60, 128], [130, 136], [200, 128],
      ].map(([x, y], i) => (
        <g key={i}>
          <rect x={x - 24} y={y - 18} width="48" height="36" rx="10" fill={ROYAL} stroke={TINT50} />
          <circle cx={x} cy={y} r="7" fill={i % 2 ? TINT20 : TINT50} />
        </g>
      ))}
      <rect x="106" y="72" width="48" height="36" rx="10" fill={o} stroke={TINT50} />
    </svg>
  );
}

export function PlatformGraphic({ name, className, accent = true }: Props) {
  // In a hero the orange CTA and <Mark> underline already carry orange; the focal element
  // drops to Royal Indigo so the view keeps two orange marks, as on ariviti.com.
  const o = accent ? ORANGE : ROYAL;
  const inner = (() => {
    switch (name) {
      case "workflow":
        return <Workflow o={o} />;
      case "agents":
        return <Agents o={o} />;
      case "squads":
        return <Squads o={o} />;
      case "rag":
        return <Rag o={o} />;
      case "integrations":
        return <Integrations o={o} />;
      case "packs":
        return <Packs o={o} />;
      case "shield":
        return <Shield o={o} />;
      case "menu-platform":
        return <MenuPlatform o={o} />;
      case "menu-solutions":
        return <MenuSolutions o={o} />;
    }
  })();
  return <div className={className}>{inner}</div>;
}
